class QuickView {
  constructor() {
    this.modal = document.querySelector('.quick-view-modal');
    this.overlay = document.querySelector('.quick-view--page-overlay');
    this.container = document.querySelector('.quick-view-modal__container');
    this.wrapper = document.querySelector('.quick-view-modal__wrapper');
    this.content = document.querySelector('.quick-view-modal__content');
    this.loader = document.querySelector('.quick-view-modal__loader');
    this.closeButton = document.querySelector('.quick-view-modal__close');
    this.body = document.body;
    this.isOpen = false;
    this.currentTrigger = null;
    this.abortController = null;

    this.init();
  }

  init() {
    // Attach click handlers to all Quick View buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('.card__quick-view-button')) {
        e.preventDefault();
        e.stopPropagation();
        const button = e.target.closest('.card__quick-view-button');
        const productUrl = button.getAttribute('data-product-url');
        const cardLink = button.closest('.card__picture-container')?.querySelector('a.card');
        this.currentTrigger = cardLink;
        this.openModal(productUrl);
      }
    });

    // Close button handler
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => {
        this.closeModal();
      });
    }

    // Overlay click handler
    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.closeModal();
      });
    }

    // ESC key handler
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeModal();
      }
    });

    // Tab trap for accessibility
    if (this.container) {
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && this.isOpen) {
          const focusableElements = this.getFocusableElements();
          const first = focusableElements[0];
          const last = focusableElements[focusableElements.length - 1];

          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last?.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first?.focus();
          }
        }
      });
    }
  }

  getFocusableElements() {
    const selector = 'button, [href], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(this.container.querySelectorAll(selector)).filter(
      (el) => !el.hasAttribute('disabled') && el.tabIndex >= 0
    );
  }

  openModal(productUrl) {
    if (!productUrl) return;
    if (!this.modal || !this.overlay || !this.container) {
      console.error('Quick View modal elements not found');
      return;
    }

    this.isOpen = true;
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    // Show modal and overlay
    this.modal.classList.remove('hidden');
    this.body.classList.add('quick-view-open');
    this.overlay.classList.add('quick-view--page-overlay--open');
    this.container.classList.add('quick-view-modal__container--open');

    // Show loader, hide content
    this.loader?.classList.remove('hidden');
    this.content.innerHTML = '';

    // Set close button focusable
    this.closeButton?.setAttribute('tabindex', '0');

    // Fetch product data
    fetch(productUrl, { signal })
      .then((res) => res.text())
      .then((html) => {
        this.loader?.classList.add('hidden');
        this.parseAndDisplayProduct(html, productUrl);
        this.closeButton?.focus();
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Quick View fetch error:', error);
          this.loader?.classList.add('hidden');
          this.content.innerHTML = '<p>Error loading product. Please try again.</p>';
        }
      });
  }

  parseAndDisplayProduct(html, productUrl) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Find product elements
    const productCard = doc.querySelector('.wt-product');
    const productInfo = doc.querySelector('.wt-product__info');
    const productMain = doc.querySelector('.wt-product__main');
    const gallery = doc.querySelector('gallery-section');

    if (!productCard || !productInfo) {
      this.content.innerHTML = '<p>Unable to load product information.</p>';
      return;
    }

    // Clone elements for manipulation
    const productCardClone = productCard.cloneNode(true);
    const productInfoClone = productInfo.cloneNode(true);

    // Remove elements we don't want in Quick View
    this.removeElements(productCardClone, [
      'collapsible-section',
      '[not-quick-add]',
      '.product__inventory',
      '.share-icons__container',
      '#gallery-loader',
      'pickup-availability',
      '.wt-product__sku',
      '.wt-product__feature-icons',
    ]);

    // Get product title
    const titleElement = productInfoClone.querySelector('.wt-product__name');
    let title = '';
    if (titleElement) {
      title = titleElement.textContent.trim();
      // Make title a link
      const titleLink = document.createElement('a');
      titleLink.href = productUrl;
      titleLink.textContent = title;
      titleLink.className = 'quick-view-modal__title-link';
      titleElement.innerHTML = '';
      titleElement.appendChild(titleLink);
    }

    // Get product price
    const priceElement = productInfoClone.querySelector('.wt-product__price');

    // Get product description (first paragraph or short description)
    const descriptionElement = productInfoClone.querySelector('.wt-product__description') ||
                               productInfoClone.querySelector('.product__description') ||
                               productInfoClone.querySelector('.rte p');

    // Get first product image from gallery
    let firstImage = null;
    if (gallery) {
      const firstImageElement = gallery.querySelector('img');
      if (firstImageElement) {
        firstImage = firstImageElement.cloneNode(true);
        firstImage.removeAttribute('srcset');
        firstImage.style.width = '100%';
        firstImage.style.height = 'auto';
      }
    }

    // Build modal content structure
    const modalContent = document.createElement('div');
    modalContent.className = 'quick-view-modal__product';

    // Image section
    if (firstImage) {
      const imageContainer = document.createElement('div');
      imageContainer.className = 'quick-view-modal__image';
      imageContainer.appendChild(firstImage);
      modalContent.appendChild(imageContainer);
    }

    // Info section
    const infoContainer = document.createElement('div');
    infoContainer.className = 'quick-view-modal__info';

    // Title
    if (titleElement) {
      infoContainer.appendChild(titleElement.cloneNode(true));
    }

    // Price
    if (priceElement) {
      const priceContainer = document.createElement('div');
      priceContainer.className = 'quick-view-modal__price';
      priceContainer.appendChild(priceElement.cloneNode(true));
      infoContainer.appendChild(priceContainer);
    }

    // Description (truncated)
    if (descriptionElement) {
      const descContainer = document.createElement('div');
      descContainer.className = 'quick-view-modal__description';
      const descText = descriptionElement.textContent.trim();
      const truncatedDesc = descText.length > 200 ? descText.substring(0, 200) + '...' : descText;
      descContainer.textContent = truncatedDesc;
      infoContainer.appendChild(descContainer);
    }

    // Variant options
    const variantOptions = productInfoClone.querySelector('variant-options');
    if (variantOptions) {
      const variantContainer = document.createElement('div');
      variantContainer.className = 'quick-view-modal__variants';
      variantContainer.appendChild(variantOptions.cloneNode(true));
      infoContainer.appendChild(variantContainer);
    }

    // Add to cart form
    const addToCartForm = productInfoClone.querySelector('form[data-type="add-to-cart-form"]');
    if (addToCartForm) {
      const formContainer = document.createElement('div');
      formContainer.className = 'quick-view-modal__form';
      formContainer.appendChild(addToCartForm.cloneNode(true));
      infoContainer.appendChild(formContainer);
    }

    // View full details link
    const viewDetailsLink = document.createElement('a');
    viewDetailsLink.href = productUrl;
    viewDetailsLink.className = 'quick-view-modal__view-details';
    viewDetailsLink.textContent = 'View Full Details';
    infoContainer.appendChild(viewDetailsLink);

    modalContent.appendChild(infoContainer);
    this.content.appendChild(modalContent);

    // Initialize variant options if present
    if (variantOptions) {
      this.initVariantOptions();
    }
  }

  initVariantOptions() {
    // Re-initialize variant dropdowns if needed
    const variantSelects = this.content.querySelectorAll('variant-options');
    variantSelects.forEach((select) => {
      // Variant options should work if the component is already initialized
      // May need to trigger re-initialization depending on theme setup
    });
  }

  removeElements(parent, selectors) {
    selectors.forEach((selector) => {
      const elements = parent.querySelectorAll(selector);
      elements.forEach((el) => el.remove());
    });
  }

  closeModal() {
    if (!this.isOpen) return;

    this.isOpen = false;

    // Abort any ongoing fetch
    if (this.abortController) {
      this.abortController.abort();
    }

    // Hide modal and overlay
    this.modal?.classList.add('hidden');
    this.body.classList.remove('quick-view-open');
    this.overlay?.classList.remove('quick-view--page-overlay--open');
    this.container?.classList.remove('quick-view-modal__container--open');

    // Clear content
    this.content.innerHTML = '';
    this.loader?.classList.remove('hidden');

    // Set close button not focusable
    this.closeButton?.setAttribute('tabindex', '-1');

    // Return focus to trigger element
    this.currentTrigger?.focus();
    this.currentTrigger = null;
  }
}

// Initialize Quick View when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new QuickView();
  });
} else {
  new QuickView();
}
